import { getDistance, navLegStruct, round } from "../plugins/opsctrl-foc/class/navLogReport";
import type { aircraftStruct } from "../plugins/opsctrl-foc/class/providers/fppProvider";
import {
  flightlistStruct,
  opsMissionTypeEnum,
  parameterStruct,
  processResultStateEnum,
  property,
} from "../plugins/opsctrl-foc/dataStruct";
import { checkPointIsRouteRelevant, formatTimespanHHmm, getParameterOfArray } from "../plugins/opsctrl-foc/functions";
import { flowRouteProgress } from "../services/ops-control/flow-route-progress";
import {
  breifgFlowAnnunWarningTypeEnum,
  briefgFlowAnnunciatorStruct,
  briefgFlowContentEnum,
  briefgFlowContStruct,
  briefgFlowFlightParamStruct,
  briefgFlowFlightStatusEnum,
  briefgFlowFlightStruct,
  briefgFlowRouteStruct,
  geoQueryResStruct,
} from "./briefing-flow.model";

/**
 * The pure half of the briefing card: everything here turns rows the loader
 * already read into what the card shows. Nothing here reads a database.
 */

const METER2NM = 0.000539957;
const METER2FEET = 3.28084;

/** A flight stays on the card until 20 minutes after its planned arrival. */
const ARRIVAL_MARGIN_MS = 20 * 60000;

/** Webcams and METARs within half this many metres of the route are shown. */
export const GEO_CORRIDOR_WIDTH_M = 20;

/** Degrees added on every side of a leg's box before the geo content is matched. */
export const GEO_LEG_BOX_MARGIN_DEG = 0.5;

/** An annunciator row, tagged with the table it came from. */
export interface AnnunciatorRow {
  kind: "fuel" | "loading" | "perf" | "nav" | "met" | "notam";
  flightId: string;
  processResultState: processResultStateEnum;
  annunciators: string;
  opsMissionType: opsMissionTypeEnum | null;
}

/** A tool-feature row as `toolFeaturesInBoundsByKeys` returns it. */
export interface GeoFeatureRow {
  id: number;
  key: string;
  value: string;
  effectiveFrom: string;
  validUntil: string;
  minX: number;
  minY: number;
  maxX: number;
  maxY: number;
}

/** Everything the card needs for one flight, already loaded. */
export interface BriefingFlightInputs {
  flight: flightlistStruct;
  params: parameterStruct[];
  routeDistanceFromOriginToDest: number;
  routeDistanceDestToAlternate: number;
  legs: navLegStruct[];
  /** Undefined when the read failed: the flight then shows without chips and parameters. */
  annunciators: AnnunciatorRow[] | undefined;
  acft: aircraftStruct;
  webcams: GeoFeatureRow[];
  metars: GeoFeatureRow[];
  /** Webcam thumbnails by tool-feature id. */
  thumbnails: Map<number, string>;
}

export interface BriefingBuildContext {
  now: number;
  webcamLiveUrl: string;
  /** [lon, lat] of the aircraft, or empty. */
  position: number[];
}

export function compareEstTimeDep(a: flightlistStruct, b: flightlistStruct): number {
  if (a.estTimeDep < b.estTimeDep) return -1;
  if (a.estTimeDep > b.estTimeDep) return 1;
  return 0;
}

function fplStateOf(flight: flightlistStruct, params: parameterStruct[]): parameterStruct[] {
  return getParameterOfArray(flight.flightId, "flightlist", property.fplState, params);
}

/** A flight plan that is filed or departed keeps its flight on the card. */
function fplIsOpen(fplState: parameterStruct[]): boolean {
  return fplState.length > 0 && (fplState[0].value == "departed" || fplState[0].value == "filed");
}

/** Whether the card shows this flight at all. */
export function isBriefingRelevant(flight: flightlistStruct, params: parameterStruct[], now: number): boolean {
  return flight.estTimeArr.getTime() + ARRIVAL_MARGIN_MS > now || fplIsOpen(fplStateOf(flight, params));
}

/** The same rule for a reader that holds only the flight's id and params (the
 *  wallboard views). A flight with no planned arrival yet counts as shown: its
 *  params can arrive after its row. */
export function isBriefingRelevantById(flightId: string, params: parameterStruct[], now: number): boolean {
  const est = getParameterOfArray(flightId, "flightlist", property.estTimeArr, params)[0]?.value;
  if (est === undefined || est === null || est === "") return true;
  return (
    parseInt(String(est)) + ARRIVAL_MARGIN_MS > now ||
    fplIsOpen(getParameterOfArray(flightId, "flightlist", property.fplState, params))
  );
}

const ANNUNCIATOR_ORDER: [AnnunciatorRow["kind"], breifgFlowAnnunWarningTypeEnum][] = [
  ["fuel", breifgFlowAnnunWarningTypeEnum.fuel],
  ["perf", breifgFlowAnnunWarningTypeEnum.perf],
  ["loading", breifgFlowAnnunWarningTypeEnum.mAndB],
  ["nav", breifgFlowAnnunWarningTypeEnum.navig],
  ["met", breifgFlowAnnunWarningTypeEnum.wx],
  ["notam", breifgFlowAnnunWarningTypeEnum.notam],
];

/** Only these tables have one result per ops mission type. */
const MISSION_KINDS = new Set<AnnunciatorRow["kind"]>(["fuel", "perf", "loading"]);

const WARN_TYPE: Partial<Record<processResultStateEnum, string>> = {
  [processResultStateEnum.fail]: "danger",
  [processResultStateEnum.warn]: "warning",
  [processResultStateEnum.ok]: "unread",
};

/**
 * The annunciator chips of one flight, in table order: fuel, performance,
 * mass and balance, navigation, weather, NOTAM. A designator shows once.
 *
 * Throws on a row whose `annunciators` is NULL, as the old code did; the
 * caller catches it and the flight shows without chips.
 */
export function annunciatorsOf(rows: AnnunciatorRow[]): briefgFlowAnnunciatorStruct[] {
  const list: briefgFlowAnnunciatorStruct[] = [];
  const seen = new Set<string>();
  const push = (a: briefgFlowAnnunciatorStruct) => {
    if (seen.has(a.desig)) return;
    list.push(a);
    seen.add(a.desig);
  };

  for (const [kind, type] of ANNUNCIATOR_ORDER) {
    const ofKind = rows.filter((r) => r.kind === kind);
    for (const row of ofKind) {
      for (const desig of row.annunciators.split(";")) {
        if (desig == "") continue;
        const a = new briefgFlowAnnunciatorStruct();
        a.type = type;
        a.desig = desig;
        if (MISSION_KINDS.has(kind)) a.opsMissionType = row.opsMissionType;
        const warnType = WARN_TYPE[row.processResultState];
        if (warnType !== undefined) a.warnType = warnType;
        push(a);
      }
    }

    if (kind === "perf" && ofKind.length === 0) {
      // No perf row at all. Since initPerformance refuses to run without a
      // live mass and balance, this is a REASON rather than an absence — and
      // an absent annunciator reads as "nothing to report", which is the
      // opposite of the truth. Say it.
      //
      // The query also excludes `processResultState = unset` and a flight
      // mid-calculation has no perf row yet, so this can fire where a perf row
      // is merely not ready. That is the fail-safe direction — an over-eager
      // warning, never a hidden one — and the other tables stay silent by
      // design because none of them is deliberately skipped the way perf is.
      const noPerf = new briefgFlowAnnunciatorStruct();
      noPerf.type = breifgFlowAnnunWarningTypeEnum.perf;
      noPerf.desig = "PERF N/A";
      noPerf.warnType = "danger";
      // The chip is tappable (`toAnyMarkdownPage(flightId, opsMissionType,
      // type)`); `all` keeps that call well-formed. There is no perf row to
      // open, so the report page explains the absence — see
      // `performanceReport.getPerformanceHtmlReport`.
      noPerf.opsMissionType = opsMissionTypeEnum.all;
      push(noPerf);
    }
  }
  return list;
}

/**
 * The box of one leg, grown by `GEO_LEG_BOX_MARGIN_DEG` on each side — what
 * the card asks the geo content for.
 */
export function legBox(from: number[], to: number[]): { minX: number; maxX: number; minY: number; maxY: number } {
  return {
    minX: Math.min(from[0], to[0]) - GEO_LEG_BOX_MARGIN_DEG,
    maxX: Math.max(from[0], to[0]) + GEO_LEG_BOX_MARGIN_DEG,
    minY: Math.min(from[1], to[1]) - GEO_LEG_BOX_MARGIN_DEG,
    maxY: Math.max(from[1], to[1]) + GEO_LEG_BOX_MARGIN_DEG,
  };
}

/**
 * The geo features along a route, leg by leg, with their distance along the
 * track and from it.
 *
 * `rows` may come from a box larger than any leg's: each leg keeps only the
 * rows that overlap its own box, with the same test the per-leg query used.
 * A feature near two legs is listed once per leg, as it always was.
 */
export function geoContentAlongLegs(
  rows: GeoFeatureRow[],
  legs: navLegStruct[],
  corridorWidth: number
): geoQueryResStruct[] {
  const out: geoQueryResStruct[] = [];
  let growingTotalDist = 0;

  for (let legId = 1; legId < legs.length; legId++) {
    const [x1, y1] = legs[legId - 1].geoPos;
    const [x2, y2] = legs[legId].geoPos;
    const box = legBox([x1, y1], [x2, y2]);

    for (const q of rows) {
      if (q.maxX < box.minX || q.minX > box.maxX || q.maxY < box.minY || q.minY > box.maxY) continue;

      // Do not show features with a huge extent: they appear on the briefing
      // section statically anyway. Nothing bigger than about a degree.
      const sizeLimit = 1000; // square degrees
      const size = Math.abs((q.maxX - q.minX) * (q.maxY - q.minY));
      if (size >= sizeLimit) continue;

      const geoPos = [(q.minX + q.maxX) / 2, (q.minY + q.maxY) / 2];
      const distFromLine = checkPointIsRouteRelevant([x1, y1], [x2, y2], geoPos, true);
      if (distFromLine.distFromRoute >= corridorWidth / 2) continue;

      const item = new geoQueryResStruct();
      item.geoPos = geoPos;
      item.feature = q;
      item.trackDist = growingTotalDist + Math.round(distFromLine.trackDist);
      item.routeOffsetDist = Math.round(distFromLine.distFromRoute);
      out.push(item);
    }

    growingTotalDist += getDistance([x1, y1], [x2, y2]);
  }
  return out;
}

/** "N min" since the feature was published, or "OLD" after 58 minutes. */
function ageText(effectiveFrom: string, now: number): string {
  const minutes = Math.round(Math.abs(parseInt(effectiveFrom) - now) / (1000 * 60));
  return minutes > 58 ? "OLD" : minutes + " min";
}

/** Yellow when the feature's validity ended. */
function outdatedCss(validUntil: string, now: number): string {
  return now > parseInt(validUntil) ? "background-color:yellow;color:black;" : "";
}

function webcamContent(
  webcams: geoQueryResStruct[],
  thumbnails: Map<number, string>,
  ctx: BriefingBuildContext
): briefgFlowContStruct[] {
  const out: briefgFlowContStruct[] = [];
  try {
    for (const each of webcams) {
      const obj = JSON.parse(each.feature.value);
      const c = {} as briefgFlowContStruct;
      c.title = obj.name + " (" + round(each.routeOffsetDist * METER2NM, 2) + " nm from route)";
      c.trackDist = each.trackDist;
      c.type = briefgFlowContentEnum.webcam;
      c.parameters = [
        ctx.webcamLiveUrl.replace("{id}", each.feature.id).toString() + "?time=" + ctx.now.toString(),
        obj.name,
        each.feature.id,
        thumbnails.get(each.feature.id),
        ageText(each.feature.effectiveFrom, ctx.now),
        outdatedCss(each.feature.validUntil, ctx.now),
      ];
      out.push(c);
    }
  } catch (err) {
    // A webcam whose JSON does not parse ends the list, as it always did.
    console.log("err:fbr= flow briefing content error " + err);
  }
  return out;
}

function metarContent(metars: geoQueryResStruct[], now: number): briefgFlowContStruct[] {
  return metars.map((each) => {
    const c = {} as briefgFlowContStruct;
    c.title = round(each.routeOffsetDist * METER2NM, 2) + " nm from route";
    c.trackDist = each.trackDist;
    c.type = briefgFlowContentEnum.metar;
    c.parameters = [
      each.feature.value,
      ageText(each.feature.effectiveFrom, now),
      outdatedCss(each.feature.validUntil, now),
    ];
    return c;
  });
}

function statusOf(
  flight: flightlistStruct,
  fplOpen: boolean,
  lastBriefing: string
): { status: briefgFlowFlightStatusEnum; msg: string } {
  if (flight.actBlocksOnTime !== undefined && fplOpen) {
    return { status: briefgFlowFlightStatusEnum.alert, msg: "FPL not yet closed! " };
  }
  if (flight.actBlocksOnTime !== undefined) return { status: briefgFlowFlightStatusEnum.archive, msg: "arrived" };
  if (flight.actBlocksOffTime !== undefined) return { status: briefgFlowFlightStatusEnum.active, msg: "active" };
  return {
    status: briefgFlowFlightStatusEnum.planned,
    msg: "planned EOBT at: " + flight.estTimeDep_gui + " : " + lastBriefing,
  };
}

function lastBriefingText(flight: flightlistStruct, params: parameterStruct[], now: number): string {
  const created = getParameterOfArray(flight.flightId, "flightlist", property.lastBriefingCreation, params);
  if (created.length === 0) {
    console.log("warn: could not read last briefing time");
    return "";
  }
  const minutes = Math.floor((now - parseInt(created[0].value)) / 1000 / 60);
  return minutes == 0 ? "briefing updated just now" : "briefing updated " + minutes + " min ago";
}

function param(name: string, value: string, type?: property): briefgFlowFlightParamStruct {
  const p = new briefgFlowFlightParamStruct();
  if (type !== undefined) p.type = type;
  p.name = name;
  p.value = value;
  return p;
}

/**
 * The flight parameters row. It is built in order and stops at the first
 * value it cannot compute (a navlog with fewer than two legs), keeping the
 * values before it, as the card always did.
 */
function flightParametersOf(
  flight: flightlistStruct,
  legs: navLegStruct[],
  fplState: parameterStruct[]
): briefgFlowFlightParamStruct[] {
  const out: briefgFlowFlightParamStruct[] = [];
  try {
    out.push(
      param(
        "ETE",
        formatTimespanHHmm(flight.estTimeArr.getTime() - flight.estTimeDep.getTime()),
        property.fplEstTimeEnr
      )
    );
    out.push(param("ETD", flight.estTimeDep_gui, property.estTimeDep));
    out.push(param("ETA", flight.estTimeArr_gui, property.estTimeArr));

    const originElev = round(legs[0].elevation * METER2FEET, 0);
    const destElev = round(legs[legs.length - 2].elevation * METER2FEET, 0);
    const deltaElev = destElev - originElev;
    out.push(param("DELTA ELEV", `${(deltaElev >= 0 ? "+" : "") + deltaElev.toString()} ft`, property.estTimeArr));
    out.push(param("DEST ELEV", destElev.toString() + " ft"));

    const track = (legs[1].intitialTrueTrack + 360) % 360;
    out.push(param("BRG (TRUE)", Math.round(track).toString().padStart(3, "0") + "°"));

    out.push(param("FPL State", fplState.length == 0 ? "virgin" : fplState[0].value, property.fplState));
  } catch (err) {
    console.log("ERR reading flight data (flowBriefing)");
  }
  return out;
}

function routeOf(legs: navLegStruct[]): briefgFlowRouteStruct[] {
  return legs.map((leg) => {
    const itm = {} as briefgFlowRouteStruct;
    itm.trackDist = leg.totalDistance;
    itm.geoPos = [...leg.geoPos];
    itm.remark = "LON: " + round(leg.geoPos[0], 3).toString() + ", " + "LAT: " + round(leg.geoPos[1], 3).toString();
    itm.desig = leg.ident;
    return itm;
  });
}

function sortByTrackDist(a: { trackDist: number }, b: { trackDist: number }): number {
  if (a.trackDist < b.trackDist) return -1;
  if (a.trackDist > b.trackDist) return 1;
  return 0;
}

/**
 * The card entry of one flight, without the terrain and obstacle profiles.
 *
 * Throws where the old per-flight body threw outside its inner catches — a
 * leg without a position — and the caller then leaves the flight out.
 */
export function buildBriefingFlight(inputs: BriefingFlightInputs, ctx: BriefingBuildContext): briefgFlowFlightStruct {
  const { flight, params, legs } = inputs;
  const fplState = fplStateOf(flight, params);
  const cObj = {} as briefgFlowFlightStruct;

  const { status, msg } = statusOf(flight, fplIsOpen(fplState), lastBriefingText(flight, params, ctx.now));
  cObj.status = status;
  cObj._statusMsg = msg;

  try {
    if (inputs.annunciators === undefined) throw new Error("annunciators not loaded");
    cObj.annunciators = annunciatorsOf(inputs.annunciators);
    cObj.flightParameters = flightParametersOf(flight, legs, fplState);
  } catch (err) {
    console.log("ERR reading flight data (flowBriefing)");
  }

  cObj.trackDistAndAlternate = inputs.routeDistanceFromOriginToDest + inputs.routeDistanceDestToAlternate;
  cObj.alternateDist = inputs.routeDistanceDestToAlternate;

  const route = routeOf(legs);
  cObj._progress = flowRouteProgress(route, ctx.position, cObj.trackDistAndAlternate - cObj.alternateDist) ?? 0;

  const content = [
    ...webcamContent(geoContentAlongLegs(inputs.webcams, legs, GEO_CORRIDOR_WIDTH_M), inputs.thumbnails, ctx),
    ...metarContent(geoContentAlongLegs(inputs.metars, legs, GEO_CORRIDOR_WIDTH_M), ctx.now),
  ];
  content.sort(sortByTrackDist);

  cObj.route = route;
  try {
    cObj.dest_gui = getParameterOfArray(flight.flightId, "flightlist", property.destName, params)[0].value;
    cObj.origin_gui = getParameterOfArray(flight.flightId, "flightlist", property.originName, params)[0].value;
  } catch (err) {
    console.log("flowBriefing:err load documents!" + err);
  }

  cObj.flightId = flight.flightId;
  cObj.flightNumber = flight.flightNumber;
  cObj.briefingContent = content;
  cObj.acft = inputs.acft;
  cObj.legs = legs;
  return cObj;
}
